interface FetchConfig extends RequestInit {
  url: string;
  data?: unknown;
  params?: Record<string, string | number | boolean | undefined>;
}

interface CustomFetchInstance {
  request: <T>(config: FetchConfig) => Promise<T>;
  get: <T>(url: string, config?: Omit<FetchConfig, 'url'>) => Promise<T>;
  delete: <T>(url: string, config?: Omit<FetchConfig, 'url'>) => Promise<T>;
  head: <T>(url: string, config?: Omit<FetchConfig, 'url'>) => Promise<T>;
  options: <T>(url: string, config?: Omit<FetchConfig, 'url'>) => Promise<T>;
  post: <T>(
    url: string,
    data?: unknown,
    config?: Omit<FetchConfig, 'url' | 'data'>
  ) => Promise<T>;
  put: <T>(
    url: string,
    data?: unknown,
    config?: Omit<FetchConfig, 'url' | 'data'>
  ) => Promise<T>;
  patch: <T>(
    url: string,
    data?: unknown,
    config?: Omit<FetchConfig, 'url' | 'data'>
  ) => Promise<T>;
}

const createFetchInstance = (baseURL: string): CustomFetchInstance => {
  const handleResponse = async <T>(response: Response): Promise<T> => {
    if (!response.ok) {
      if (response.status === 401) {
        if (
          typeof window !== 'undefined' &&
          !window.location.href.includes('/login')
        ) {
          const redirect = encodeURIComponent(
            window.location.pathname + window.location.search
          );
          window.location.href = `/login?redirect=${redirect}`;
        }
      }
      const err = await response.json();
      throw new Error(err.message || 'An error occurred while fetching data');
    }
    if (response.status === 204) {
      return {} as T;
    }
    return response.json() as Promise<T>;
  };

  const request = async <T>({
    url,
    data,
    params,
    ...config
  }: FetchConfig): Promise<T> => {
    const fullUrl = params
      ? `${baseURL}${url}?${new URLSearchParams(
          Object.fromEntries(
            Object.entries(params)
              .filter(([, v]) => v !== undefined)
              .map(([k, v]) => [k, String(v)])
          )
        ).toString()}`
      : `${baseURL}${url}`;
    const response = await fetch(fullUrl, {
      ...config,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...config.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    });

    return handleResponse<T>(response);
  };

  const get = <T>(url: string, config?: Omit<FetchConfig, 'url'>): Promise<T> =>
    request<T>({ url, method: 'get', ...config });
  const deleteRequest = <T>(
    url: string,
    config?: Omit<FetchConfig, 'url'>
  ): Promise<T> => request<T>({ url, method: 'delete', ...config });
  const head = <T>(
    url: string,
    config?: Omit<FetchConfig, 'url'>
  ): Promise<T> => request<T>({ url, method: 'head', ...config });
  const options = <T>(
    url: string,
    config?: Omit<FetchConfig, 'url'>
  ): Promise<T> => request<T>({ url, method: 'options', ...config });

  const post = <T>(
    url: string,
    data?: unknown,
    config?: Omit<FetchConfig, 'url' | 'data'>
  ): Promise<T> => request<T>({ url, method: 'post', data, ...config });

  const put = <T>(
    url: string,
    data?: unknown,
    config?: Omit<FetchConfig, 'url' | 'data'>
  ): Promise<T> => request<T>({ url, method: 'put', data, ...config });

  const patch = <T>(
    url: string,
    data?: unknown,
    config?: Omit<FetchConfig, 'url' | 'data'>
  ): Promise<T> => request<T>({ url, method: 'PATCH', data, ...config });

  return {
    request,
    get,
    delete: deleteRequest,
    head,
    options,
    post,
    put,
    patch,
  };
};

export const fetchInstance = createFetchInstance(
  `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/api/`
);
